/**
 * Exact port of the native slab color pipeline (CardGradeScreen.tsx,
 * brent/cool-slab): deriveHeroPaletteFromPixels samples the card photo
 * (32px wide) for accent/support/highlight clusters, normalizes them in
 * HSL, and every slab tint (body glow, edge metal, glass glare) derives
 * from the result. `deriveSlabPaletteFromImage` runs the same math on a
 * canvas; `slabPalette(hex)` is the synchronous fallback seeded from one
 * color (used for SSR/first paint and CORS-blocked images).
 */

export interface RgbColor {
  r: number
  g: number
  b: number
}

const HERO_PALETTE_SAMPLE_WIDTH = 32
const HERO_PALETTE_BASE_COLOR: RgbColor = { r: 15, g: 14, b: 16 }
const FALLBACK_PRIMARY: RgbColor = { r: 70, g: 66, b: 78 }

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

/* ---------- color helpers (hue in DEGREES, like the app) ---------- */

function rgbToHsl({ r, g, b }: RgbColor): { h: number; s: number; l: number } {
  const r0 = r / 255
  const g0 = g / 255
  const b0 = b / 255
  const max = Math.max(r0, g0, b0)
  const min = Math.min(r0, g0, b0)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === r0) h = ((g0 - b0) / d + (g0 < b0 ? 6 : 0)) / 6
  else if (max === g0) h = ((b0 - r0) / d + 2) / 6
  else h = ((r0 - g0) / d + 4) / 6
  return { h: h * 360, s, l }
}

function hslToRgb(hDeg: number, s: number, l: number): RgbColor {
  const h = ((hDeg % 360) + 360) % 360 / 360
  if (s === 0) {
    const v = Math.round(l * 255)
    return { r: v, g: v, b: v }
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const channel = (t0: number) => {
    let t = t0
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  return {
    r: Math.round(channel(h + 1 / 3) * 255),
    g: Math.round(channel(h) * 255),
    b: Math.round(channel(h - 1 / 3) * 255),
  }
}

const mixRgb = (a: RgbColor, b: RgbColor, t: number): RgbColor => ({
  r: a.r + (b.r - a.r) * t,
  g: a.g + (b.g - a.g) * t,
  b: a.b + (b.b - a.b) * t,
})

const rgba = ({ r, g, b }: RgbColor, a: number) =>
  `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`

export function hexToRgb(hex?: string): RgbColor | null {
  if (!hex) return null
  const m = hex.replace('#', '')
  if (m.length !== 6) return null
  return {
    r: parseInt(m.slice(0, 2), 16),
    g: parseInt(m.slice(2, 4), 16),
    b: parseInt(m.slice(4, 6), 16),
  }
}

/* ---------- native swatch scoring helpers ---------- */

const getColorSpan = (c: RgbColor) =>
  Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b)

function getHueDistance(first: number, second: number) {
  const difference = Math.abs(first - second) % 360
  return difference > 180 ? 360 - difference : difference
}

function isMutedEarthTone(hue: number, saturation: number, lightness: number, colorSpan: number) {
  const isBrown =
    hue >= 12 && hue <= 58 && saturation < 0.62 &&
    lightness >= 0.14 && lightness <= 0.58 && colorSpan < 118
  const isOlive =
    hue > 58 && hue <= 92 && saturation < 0.58 &&
    lightness >= 0.16 && lightness <= 0.58 && colorSpan < 128
  return isBrown || isOlive
}

/* ---------- palette assembly (app's deriveHeroPaletteFromPixels tail) ---------- */

export interface SlabPalette {
  bodyTop: string // face radial glow @ 0.41 (cardGradient[0])
  bodyMid: string // face radial glow @ 0.28 (cardGradient[1])
  bodyBottom: string // face radial glow transparent tail (cardGradient[2])
  pageGlow: string // stronger card-derived glow for share preview backgrounds
  pageMid: string
  borderTop: string // 1px bevel ring, accent-tinted @ 0.22
  borderBottom: string // 1px bevel ring, near-white @ 0.15
}

function paletteFromSeeds(
  accentColor: RgbColor,
  supportColor: RgbColor,
  highlightColor: RgbColor,
): SlabPalette {
  const accentHsl = rgbToHsl(accentColor)
  const supportHsl = rgbToHsl(supportColor)
  const highlightHsl = rgbToHsl(highlightColor)

  const normalizedAccent = hslToRgb(
    accentHsl.h,
    clamp(accentHsl.s * 1.15, 0.5, 0.85),
    clamp(accentHsl.l, 0.46, 0.64),
  )
  const normalizedBase = hslToRgb(
    accentHsl.h,
    clamp(accentHsl.s * 0.52, 0.24, 0.42),
    clamp((accentHsl.l + supportHsl.l) * 0.28, 0.2, 0.31),
  )
  const normalizedSupport = hslToRgb(
    supportHsl.h,
    clamp(supportHsl.s * 0.75, 0.26, 0.5),
    clamp(supportHsl.l * 0.72, 0.28, 0.42),
  )
  const normalizedHighlight = hslToRgb(
    highlightHsl.h,
    clamp(highlightHsl.s * 0.8, 0.24, 0.48),
    clamp(highlightHsl.l, 0.7, 0.9),
  )

  // Grade-screen (owen/grade-page) mixes: backgroundTop pulls 0.34 toward
  // the dark base; the bevel ring is borderTop/borderBottom at 0.22/0.15.
  const backgroundPeak = mixRgb(normalizedAccent, normalizedHighlight, 0.08)
  const backgroundTop = mixRgb(backgroundPeak, HERO_PALETTE_BASE_COLOR, 0.34)
  const backgroundMid = mixRgb(
    mixRgb(normalizedSupport, normalizedAccent, 0.44),
    HERO_PALETTE_BASE_COLOR,
    0.52,
  )
  const backgroundBottom = mixRgb(normalizedBase, HERO_PALETTE_BASE_COLOR, 0.72)
  const borderTop = mixRgb(normalizedAccent, normalizedHighlight, 0.12)
  const borderBottom = mixRgb(normalizedHighlight, { r: 255, g: 255, b: 255 }, 0.34)

  return {
    bodyTop: rgba(backgroundTop, 0.41),
    bodyMid: rgba(backgroundMid, 0.28),
    bodyBottom: rgba(backgroundBottom, 0),
    pageGlow: rgba(backgroundTop, 0.19),
    pageMid: rgba(backgroundMid, 0.1),
    borderTop: rgba(borderTop, 0.22),
    borderBottom: rgba(borderBottom, 0.15),
  }
}

/** Synchronous fallback: one seed color stands in for all three clusters */
export function slabPalette(paletteHex?: string): SlabPalette {
  const seed = hexToRgb(paletteHex) ?? FALLBACK_PRIMARY
  const seedHsl = rgbToHsl(seed)
  const highlightSeed = hslToRgb(seedHsl.h, seedHsl.s, clamp(seedHsl.l + 0.25, 0.7, 0.9))
  return paletteFromSeeds(seed, seed, highlightSeed)
}

/* ---------- the pixel sampler (deriveHeroPaletteFromPixels port) ---------- */

interface Swatch {
  color: RgbColor
  count: number
  weight: number
  centrality: number
  saturation: number
  lightness: number
  hue: number
}

export async function deriveSlabPaletteFromImage(
  imageUrl: string,
): Promise<SlabPalette | null> {
  let pixels: Uint8ClampedArray
  let width: number
  let height: number
  try {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('image load failed'))
      img.src = imageUrl
    })
    width = HERO_PALETTE_SAMPLE_WIDTH
    height = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * width))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.drawImage(img, 0, 0, width, height)
    pixels = ctx.getImageData(0, 0, width, height).data
  } catch {
    // CORS-tainted canvas or load failure — caller keeps the seed fallback
    return null
  }

  const swatchMap = new Map<
    string,
    {
      color: RgbColor
      count: number
      weightTotal: number
      centralityTotal: number
      saturationTotal: number
      lightnessTotal: number
      hueTotal: number
    }
  >()

  for (let index = 0; index <= pixels.length - 4; index += 4) {
    const pixelIndex = index / 4
    const x = pixelIndex % width
    const y = Math.floor(pixelIndex / width)
    const xRatio = width > 1 ? x / (width - 1) : 0.5
    const yRatio = height > 1 ? y / (height - 1) : 0.5

    // Focus on the artwork region and ignore most edge/border noise.
    if (xRatio < 0.06 || xRatio > 0.94 || yRatio < 0.08 || yRatio > 0.92) continue

    const alpha = pixels[index + 3]
    if (alpha < 140) continue

    const color = { r: pixels[index], g: pixels[index + 1], b: pixels[index + 2] }
    const colorSpan = getColorSpan(color)
    const { h: hue, s, l } = rgbToHsl(color)

    if (colorSpan < 34 || l < 0.18 || l > 0.84 || s < 0.3) continue
    if (s < 0.5 && colorSpan < 80) continue
    if (isMutedEarthTone(hue, s, l, colorSpan)) continue

    const quantized = {
      r: Math.round(color.r / 24) * 24,
      g: Math.round(color.g / 24) * 24,
      b: Math.round(color.b / 24) * 24,
    }
    const key = `${quantized.r}-${quantized.g}-${quantized.b}`
    const centralityWeight = clamp(
      1.22 - Math.abs(xRatio - 0.5) * 1.35 - Math.abs(yRatio - 0.52) * 1.45,
      0.18,
      1.22,
    )
    const sampleWeight = (0.82 + centralityWeight * 0.28) * (0.74 + s * 1.28)

    const current = swatchMap.get(key)
    if (current) {
      current.count += 1
      current.weightTotal += sampleWeight
      current.centralityTotal += centralityWeight
      current.saturationTotal += s * sampleWeight
      current.lightnessTotal += l * sampleWeight
      current.hueTotal += hue * sampleWeight
    } else {
      swatchMap.set(key, {
        color: quantized,
        count: 1,
        weightTotal: sampleWeight,
        centralityTotal: centralityWeight,
        saturationTotal: s * sampleWeight,
        lightnessTotal: l * sampleWeight,
        hueTotal: hue * sampleWeight,
      })
    }
  }

  const swatches: Swatch[] = Array.from(swatchMap.values()).map((swatch) => ({
    color: swatch.color,
    count: swatch.count,
    weight: swatch.weightTotal,
    centrality: swatch.centralityTotal / swatch.count,
    saturation: swatch.saturationTotal / swatch.weightTotal,
    lightness: swatch.lightnessTotal / swatch.weightTotal,
    hue: swatch.hueTotal / swatch.weightTotal,
  }))

  if (swatches.length === 0) return null

  const pickColor = (score: (s: Swatch) => number, fallbackColor: RgbColor) => {
    let bestSwatch = swatches[0]
    let bestScore = Number.NEGATIVE_INFINITY
    swatches.forEach((swatch) => {
      const currentScore = score(swatch)
      if (currentScore > bestScore) {
        bestScore = currentScore
        bestSwatch = swatch
      }
    })
    return bestSwatch?.color || fallbackColor
  }

  const baseColor = pickColor(
    (s) =>
      s.weight * (0.55 + s.centrality * 0.7) * Math.pow(s.saturation, 1.3) *
      (1.1 - Math.abs(s.lightness - 0.42)),
    HERO_PALETTE_BASE_COLOR,
  )
  const accentColor = pickColor(
    (s) =>
      Math.pow(s.weight, 0.32) * (0.9 + s.centrality * 0.1) *
      Math.pow(s.saturation, 3.7) *
      Math.pow(clamp((s.lightness - 0.18) / 0.64, 0, 1), 1.24) *
      (0.78 + (getColorSpan(s.color) / 255) * 0.65),
    baseColor,
  )
  const accentHue = rgbToHsl(accentColor).h
  const supportColor = pickColor(
    (s) =>
      Math.pow(s.weight, 0.76) * (0.88 + s.centrality * 0.12) *
      Math.pow(s.saturation, 1.28) * (1.05 - Math.abs(s.lightness - 0.36)) *
      clamp(1.18 - getHueDistance(s.hue, accentHue) / 110, 0.18, 1.18),
    accentColor,
  )
  const highlightColor = pickColor(
    (s) =>
      Math.pow(s.weight, 0.44) * (0.86 + s.centrality * 0.12) *
      Math.pow(s.saturation, 2.1) * Math.pow(clamp(s.lightness, 0, 1), 1.42) *
      (0.78 + (getColorSpan(s.color) / 255) * 0.4),
    mixRgb(accentColor, { r: 255, g: 255, b: 255 }, 0.45),
  )

  return paletteFromSeeds(accentColor, supportColor, highlightColor)
}
